import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Dialog, DialogBody, DialogFooter, InputGroup, NumericInput, Spinner, Switch } from '@blueprintjs/core';
import { toast } from 'sonner';
import {
  fetchBatchSummary, fetchOpeners, fetchReps, newIdempotencyKey, previewAssignment, saveAssignment, setOpenerRule,
  type AssignmentControls, type AssignmentPreview, type LeadFilter,
} from './api';
import { leadImportKeys } from './keys';
import { queryPolicies } from '../../utils/queryPolicies';
import { ErrorNote, Pill, Segmented, fmtInt } from './ui';
import { PickList } from '../ui/PickList';

/**
 * Which leads a plan gives out (spec v4 A3). "First time" is the default: no
 * other copy of the phone anywhere. Each rule maps to exact assignment
 * controls, which are saved with the assignment.
 */
type Rule = 'first_time' | 'chosen_sources' | 'no_rep_had' | 'everything';
const RULES: { value: Rule; label: string; hint: string }[] = [
  { value: 'first_time', label: 'First time', hint: 'Phones never received from any other import' },
  { value: 'chosen_sources', label: 'Repeats from chosen sources', hint: 'First-time leads plus repeats of the sources you choose' },
  { value: 'no_rep_had', label: 'No rep has had it', hint: 'Repeats included, unless any rep ever had another copy' },
  { value: 'everything', label: 'Everything', hint: 'Every assignable lead, repeats and leads other reps had included' },
];
const RULE_LABEL = new Map(RULES.map((r) => [r.value, r.label]));
const MAX_EACH = 300;

/** A rep's own filter (the per-rep modal, A5); null rule follows the plan's rule. */
interface RepFilter { rule: Rule | null; chosen: number[]; exclude: number[] }
interface PlanRow { rep: number; name: string; on: boolean; count: number; opener: boolean; filter: RepFilter | null }

/** overlap: every source this import shares phones with. */
function controlsFor(rule: Rule, chosen: number[], exclude: number[], overlap: number[]): AssignmentControls {
  const skip = (ids: number[]): AssignmentControls => (ids.length
    ? { duplicate_mode: 'exclude_selected_sources', source_ids: ids, history_qualifier: 'received', time_basis: 'all_known' }
    : { duplicate_mode: 'include_repeats' });
  if (rule === 'first_time') return { duplicate_mode: 'exclude_any', history_qualifier: 'received', time_basis: 'all_known', prior_rep_mode: 'none' };
  if (rule === 'chosen_sources') return { ...skip(overlap.filter((s) => !chosen.includes(s) || exclude.includes(s))), prior_rep_mode: 'none' };
  if (rule === 'no_rep_had') return { ...skip(exclude), prior_rep_mode: 'any_rep', prior_rep_history: 'ever' };
  return { ...skip(exclude), prior_rep_mode: 'none' };
}

/** One preview per set of reps that share a filter; saved one after the other. */
interface PlanPart { preview: AssignmentPreview; key: string; label: string | null }

/**
 * The default assignment plan for one import (spec v4 A5-A8): every opener is
 * checked with min(300, eligible / openers) leads, dealt out in file order.
 * With fixedRep it is the "Distribute {rep} additional 300" form instead.
 */
export default function QuickAssign({ batchId, fixedRep, onAdvanced, onSaved }: {
  batchId: number;
  fixedRep?: { rep: number; name: string };
  onAdvanced?: () => void;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const summary = useQuery({ queryKey: leadImportKeys.summary(batchId), queryFn: () => fetchBatchSummary(batchId), ...queryPolicies.pagedData });
  const openers = useQuery({ queryKey: leadImportKeys.openers, queryFn: fetchOpeners, enabled: !fixedRep, ...queryPolicies.configuration });
  const reps = useQuery({ queryKey: leadImportKeys.reps, queryFn: fetchReps, ...queryPolicies.configuration });
  const overlap = useMemo(() => (summary.data?.overlap_sources ?? []).flatMap((o) => (o.lead_source == null ? [] : [{ id: o.lead_source, name: o.source ?? '', phones: o.phones }])), [summary.data]);
  const overlapIds = useMemo(() => overlap.map((o) => o.id), [overlap]);

  const [rule, setRule] = useState<Rule>('first_time');
  const [chosen, setChosen] = useState<number[]>([]);
  const [order, setOrder] = useState<'first_n' | 'random_n'>('first_n');
  const [even, setEven] = useState(!fixedRep);
  const [rows, setRows] = useState<PlanRow[]>(() => (fixedRep ? [{ ...fixedRep, on: true, count: MAX_EACH, opener: false, filter: null }] : []));
  const [editing, setEditing] = useState<number | null>(null);
  const [settings, setSettings] = useState(false);
  const [parts, setParts] = useState<PlanPart[] | null>(null);
  const [note, setNote] = useState('');

  // The openers fill the plan once; later opener edits apply to the next plan.
  useEffect(() => {
    if (fixedRep || !openers.data || rows.length > 0) return;
    setRows(openers.data.filter((o) => o.opener).map((o) => ({ rep: o.rep, name: o.name, on: true, count: 0, opener: true, filter: null })));
  }, [openers.data, fixedRep, rows.length]);

  const viewFilter: LeadFilter = useMemo(() => ({ batch: batchId, assignment: 'unassigned' }), [batchId]);
  const baseControls = useMemo(() => controlsFor(rule, chosen, [], overlapIds), [rule, chosen, overlapIds]);
  const probeRep = rows[0]?.rep ?? (reps.data ?? []).find((r) => r.is_active)?.id;
  // How many unassigned leads pass the plan's rule; drives the default counts.
  const eligible = useQuery({
    queryKey: leadImportKeys.eligible(batchId, baseControls),
    queryFn: () => previewAssignment({ split: [{ rep: probeRep as number, count: 1 }],
      selection: { mode: 'first_n', n: 1, sort: 'origin', desc: false, view_filter: viewFilter }, controls: baseControls }),
    enabled: probeRep != null && summary.isSuccess,
    ...queryPolicies.pagedData,
  });
  const available = eligible.data?.available;
  const onRows = rows.filter((r) => r.on);
  const evenEach = available == null || onRows.length === 0 ? 0 : Math.min(MAX_EACH, Math.floor(available / onRows.length));

  // Even split keeps every checked rep on the same share of what is eligible.
  const onKey = onRows.map((r) => r.rep).join(',');
  useEffect(() => {
    if (!even || available == null) return;
    setRows((cur) => cur.map((r) => (r.on ? { ...r, count: evenEach } : r)));
  }, [even, evenEach, onKey, available]);
  // A distribute form starts on 300, or fewer when fewer are left.
  useEffect(() => {
    if (fixedRep && available != null) setRows((cur) => cur.map((r) => ({ ...r, count: Math.min(MAX_EACH, available) })));
  }, [fixedRep, available]);

  const setRow = (rep: number, patch: Partial<PlanRow>) => setRows((cur) => cur.map((r) => (r.rep === rep ? { ...r, ...patch } : r)));
  const setCount = (rep: number, count: number) => setRows((cur) => {
    if (!even || available == null) return cur.map((r) => (r.rep === rep ? { ...r, count } : r));
    // Even split: the other checked reps share what is left.
    const others = cur.filter((r) => r.on && r.rep !== rep).length;
    const each = others ? Math.min(MAX_EACH, Math.floor(Math.max(0, available - count) / others)) : 0;
    return cur.map((r) => (r.rep === rep ? { ...r, count } : r.on ? { ...r, count: each } : r));
  });

  const total = onRows.reduce((t, r) => t + r.count, 0);
  const planKey = JSON.stringify([rule, chosen, order, rows]);
  useEffect(() => { setParts(null); }, [planKey]);

  const groups = useMemo(() => {
    const byKey = new Map<string, { controls: AssignmentControls; rows: PlanRow[]; custom: boolean }>();
    for (const r of onRows.filter((x) => x.count > 0)) {
      const f = r.filter;
      const controls = f ? controlsFor(f.rule ?? rule, f.rule === 'chosen_sources' ? f.chosen : chosen, f.exclude, overlapIds) : baseControls;
      const key = JSON.stringify(controls);
      const g = byKey.get(key) ?? { controls, rows: [], custom: !!f };
      g.rows.push(r);
      byKey.set(key, g);
    }
    return [...byKey.values()];
  }, [onRows, rule, chosen, overlapIds, baseControls]);

  const doPreview = useMutation({
    mutationFn: async () => {
      // Each group draws from what earlier groups left, so no lead goes twice.
      const taken: number[] = [];
      const out: PlanPart[] = [];
      for (const g of groups) {
        const n = g.rows.reduce((t, r) => t + r.count, 0);
        const preview = await previewAssignment({
          split: g.rows.map((r) => ({ rep: r.rep, count: r.count })),
          selection: { mode: order, n, sort: 'origin', desc: false, view_filter: taken.length ? { ...viewFilter, exclude_ids: [...taken] } : viewFilter },
          controls: g.controls,
        });
        taken.push(...preview.items.map((i) => i.lead));
        out.push({ preview, key: newIdempotencyKey(), label: g.custom && groups.length > 1 ? g.rows.map((r) => r.name).join(', ') : null });
      }
      return out;
    },
    onSuccess: setParts,
    onError: (e) => toast.error((e as Error).message),
  });

  const save = useMutation({
    mutationFn: async () => {
      let applied = 0; let conflicts = 0;
      for (const p of parts ?? []) {
        const r = await saveAssignment(p.preview, p.key, false, null, note || null);
        applied += r.applied; conflicts += r.conflict + r.departed_from_draw;
      }
      return { applied, conflicts };
    },
    onSuccess: ({ applied, conflicts }) => {
      if (conflicts > 0) toast.warning(`Assigned ${fmtInt(applied)}. ${fmtInt(conflicts)} leads changed since the preview and were left untouched.`);
      else toast.success(`Assigned ${fmtInt(applied)} leads.`);
      setParts(null); setNote('');
      queryClient.invalidateQueries({ queryKey: leadImportKeys.all });
      onSaved();
    },
    onError: (e) => {
      // Earlier parts may have saved; the lists show exactly what did.
      toast.error((e as Error).message);
      queryClient.invalidateQueries({ queryKey: leadImportKeys.all });
    },
  });

  const shares = (parts ?? []).flatMap((p) => p.preview.shares);
  const selected = (parts ?? []).reduce((t, p) => t + p.preview.selected, 0);
  const first = parts?.[0]?.preview;
  const addable = (reps.data ?? []).filter((r) => r.is_active && !rows.some((x) => x.rep === r.id));
  const editRow = rows.find((r) => r.rep === editing);
  const missing = rule === 'chosen_sources' && chosen.length === 0 ? 'Choose at least one source.' : total === 0 ? 'Give at least one rep some leads.' : null;

  return (
    <div className={fixedRep ? 'li-assign li-assign--bare' : 'li-assign'} aria-label={fixedRep ? `Distribute more to ${fixedRep.name}` : 'Assignment plan'}>
      {!fixedRep && (
        <div className="li-assign__head">
          <div className="li-assign__title">
            <h3>Assign to openers</h3>
            <span className="li-assign__tools">
              <button type="button" className="li-btn li-btn--link" onClick={() => setSettings(true)}>Openers</button>
              {onAdvanced && <button type="button" className="li-btn li-btn--link" onClick={onAdvanced}>Advanced</button>}
            </span>
          </div>
          <span className="li-muted" aria-live="polite">
            {eligible.isFetching ? 'Counting eligible leads…' : available != null ? `${fmtInt(available)} eligible ${available === 1 ? 'lead is' : 'leads are'} not with a rep yet` : ''}
          </span>
        </div>
      )}
      {fixedRep && <span className="li-muted" aria-live="polite">{eligible.isFetching ? 'Counting eligible leads…' : available != null ? `${fmtInt(available)} eligible leads left in this import` : ''}</span>}

      <section className="li-assign__sec">
        <span className="li-field__label">Which leads</span>
        <PickList value={rule} onChange={(v) => setRule(v as Rule)} fill ariaLabel="Which leads" options={RULES} />
        <span className="li-muted">{RULES.find((r) => r.value === rule)?.hint}.</span>
        {rule === 'chosen_sources' && (
          <SourceChecklist label="Include repeats of" options={overlap} value={chosen} onChange={setChosen} />
        )}
      </section>

      <section className="li-assign__sec">
        <div className="li-recipients__head">
          <span className="li-field__label">{fixedRep ? 'Leads' : 'Reps'}</span>
          {!fixedRep && <Switch checked={even} onChange={(e) => setEven((e.target as HTMLInputElement).checked)} label="Even split" alignIndicator="right" className="li-switch-sm" />}
        </div>
        {openers.isLoading && <Spinner size={16} />}
        {!fixedRep && openers.isSuccess && rows.length === 0 && <span className="li-muted">No openers yet. Add a rep, or set openers.</span>}
        <ul className="li-plan" aria-label="Reps in this plan">
          {rows.map((r) => (
            <li key={r.rep} className={`li-plan__row${r.on ? '' : ' is-off'}`}>
              {!fixedRep && <Checkbox checked={r.on} onChange={() => setRow(r.rep, { on: !r.on })} aria-label={`Include ${r.name}`} className="li-plan__check" />}
              <span className="li-plan__name">
                {r.name}
                {r.filter ? <span className="li-subtag">filtered</span>
                  : !r.opener && !fixedRep && <span className="li-subtag">added</span>}
              </span>
              <NumericInput min={0} value={r.count} disabled={!r.on} buttonPosition="none" className="li-plan__count"
                onValueChange={(v) => setCount(r.rep, Math.max(0, Math.floor(v || 0)))} aria-label={`Leads for ${r.name}`} />
              {!fixedRep && <Button small minimal icon="filter" aria-label={`Filters for ${r.name}`} title="This rep's filters" onClick={() => setEditing(r.rep)} />}
            </li>
          ))}
        </ul>
        {!fixedRep && (
          <div className="li-row li-row--between">
            <PickList value="" placeholder="Add rep…" ariaLabel="Add rep" minimal disabled={!addable.length}
              options={addable.map((a) => ({ value: String(a.id), label: a.name }))}
              onChange={(v) => { const rep = (reps.data ?? []).find((x) => String(x.id) === v); if (rep) setRows((cur) => [...cur, { rep: rep.id, name: rep.name, on: true, count: 0, opener: false, filter: null }]); }} />
            <span className="li-muted">{fmtInt(total)} leads in all</span>
          </div>
        )}
        <Segmented<'first_n' | 'random_n'> ariaLabel="Pick order" size="sm" value={order} onChange={setOrder}
          options={[{ value: 'first_n', label: 'In file order', hint: 'Dealt out in turn from the top of the file' }, { value: 'random_n', label: 'Random', hint: 'Drawn at random from the eligible leads' }]} />
      </section>

      <div className="li-assign__go">
        <Button intent="primary" fill disabled={!!missing || groups.length === 0} loading={doPreview.isPending} onClick={() => doPreview.mutate()}>Preview</Button>
        {missing && <span className="li-muted">{missing}</span>}
      </div>

      {parts && first && (
        <section className="li-assign__preview" aria-live="polite" aria-label="Plan preview">
          <div className="li-assign__big"><b>{fmtInt(selected)}</b> leads to <b>{shares.length === 1 ? shares[0].name : `${shares.length} reps`}</b></div>
          <table className="li-table li-table--compact li-shares">
            <thead><tr><th>Rep</th><th className="is-num">Leads</th></tr></thead>
            <tbody>
              {shares.map((sh) => (
                <tr key={sh.rep}>
                  <td>{sh.name}</td>
                  <td className="is-num">{fmtInt(sh.count)}{sh.requested != null && sh.count < sh.requested && <span className="li-muted"> of {fmtInt(sh.requested)}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <dl className="li-dl li-dl--tight">
            <dt>Not with a rep</dt><dd>{fmtInt(first.considered)}</dd>
            {first.hard_excluded > 0 && <><dt>Not assignable</dt><dd>{fmtInt(first.hard_excluded)}</dd></>}
            {first.duplicate_excluded > 0 && <><dt>Repeats left out</dt><dd>{fmtInt(first.duplicate_excluded)}</dd></>}
            {first.rep_excluded > 0 && <><dt>A rep already had</dt><dd>{fmtInt(first.rep_excluded)}</dd></>}
          </dl>
          {parts.some((p) => p.label) && <span className="li-muted">Reps with their own filter are drawn after the others: {parts.filter((p) => p.label).map((p) => p.label).join('; ')}.</span>}
          {parts.some((p) => p.preview.shortfall > 0) && <Pill tone="warn">Not enough eligible leads for every count</Pill>}
          <InputGroup value={note} placeholder="Note (optional)" onChange={(e) => setNote(e.target.value)} />
          <Button intent="success" fill disabled={selected === 0} loading={save.isPending} onClick={() => save.mutate()}>Assign {fmtInt(selected)} leads</Button>
        </section>
      )}

      {editRow && (
        <RepFilterDialog row={editRow} planRule={rule} overlap={overlap}
          onClose={() => setEditing(null)} onSave={(filter) => { setRow(editRow.rep, { filter }); setEditing(null); }} />
      )}
      {settings && <OpenersDialog onClose={() => setSettings(false)} />}
      <ErrorNote error={eligible.error ?? summary.error ?? openers.error} />
    </div>
  );
}

function SourceChecklist({ label, options, value, onChange }: {
  label: string; options: { id: number; name: string; phones: number }[]; value: number[]; onChange: (v: number[]) => void;
}) {
  return (
    <div className="li-assign__sub">
      <span className="li-field__label">{label}</span>
      {options.length === 0 ? <span className="li-muted">No lead in this import was received in another import.</span> : (
        <div className={`li-checklist${options.length > 8 ? ' li-checklist--scroll' : ''}`}>
          {options.map((o) => (
            <Checkbox key={o.id} checked={value.includes(o.id)}
              onChange={() => onChange(value.includes(o.id) ? value.filter((x) => x !== o.id) : [...value, o.id])}
              labelElement={<span>{o.name}<span className="li-muted"> {fmtInt(o.phones)} shared</span></span>} />
          ))}
        </div>
      )}
    </div>
  );
}

/** One rep's own filter: a different rule, and sources to leave out (A5). */
function RepFilterDialog({ row, planRule, overlap, onClose, onSave }: {
  row: PlanRow; planRule: Rule; overlap: { id: number; name: string; phones: number }[];
  onClose: () => void; onSave: (f: RepFilter | null) => void;
}) {
  const [rule, setRule] = useState<Rule | null>(row.filter?.rule ?? null);
  const [chosen, setChosen] = useState<number[]>(row.filter?.chosen ?? []);
  const [exclude, setExclude] = useState<number[]>(row.filter?.exclude ?? []);
  const effective = rule ?? planRule;
  const custom = rule != null || exclude.length > 0;
  return (
    <Dialog isOpen onClose={onClose} title={`Filters for ${row.name}`} style={{ width: 'min(480px, 100vw)' }}>
      <DialogBody>
        <div className="li-stack">
          <label className="li-field">
            <span className="li-field__label">Which leads</span>
            <PickList value={rule ?? ''} onChange={(v) => setRule((v || null) as Rule | null)} fill ariaLabel="Rep rule"
              options={[{ value: '', label: `Same as the plan (${RULE_LABEL.get(planRule)})` }, ...RULES]} />
          </label>
          {rule === 'chosen_sources' && <SourceChecklist label="Include repeats of" options={overlap} value={chosen} onChange={setChosen} />}
          {effective !== 'first_time' && <SourceChecklist label="Leave out repeats of" options={overlap} value={exclude} onChange={setExclude} />}
        </div>
      </DialogBody>
      <DialogFooter actions={<>
        <Button onClick={() => onSave(null)}>Use the plan&apos;s filter</Button>
        <Button intent="primary" onClick={() => onSave(custom ? { rule, chosen, exclude } : null)}>Save</Button>
      </>} />
    </Dialog>
  );
}

/** Opener settings (A10): the Opener group, plus reps added or left out by rule. */
function OpenersDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const openers = useQuery({ queryKey: leadImportKeys.openers, queryFn: fetchOpeners, ...queryPolicies.configuration });
  const reps = useQuery({ queryKey: leadImportKeys.reps, queryFn: fetchReps, ...queryPolicies.configuration });
  const set = useMutation({
    mutationFn: ({ rep, rule }: { rep: number; rule: 'include' | 'exclude' | 'default' }) => setOpenerRule(rep, rule),
    onSuccess: (data) => queryClient.setQueryData(leadImportKeys.openers, data),
    onError: (e) => toast.error((e as Error).message),
  });
  const byRep = new Map((openers.data ?? []).map((o) => [o.rep, o]));
  const listed = (reps.data ?? []).filter((r) => r.is_active)
    .map((r) => byRep.get(r.id) ?? { rep: r.id, name: r.name, in_group: false, rule: null, opener: false })
    .sort((a, b) => Number(b.opener) - Number(a.opener) || a.name.localeCompare(b.name));
  return (
    <Dialog isOpen onClose={onClose} title="Openers" style={{ width: 'min(560px, 100vw)' }}>
      <DialogBody>
        <p className="li-muted">A new plan starts with every opener checked. Openers are the Opener group, plus reps you add here, minus reps you leave out.</p>
        <ErrorNote error={openers.error ?? reps.error} />
        <div className="li-tablewrap li-tablewrap--scroll">
          <table className="li-table li-table--compact li-table--middle" aria-label="Openers">
            <thead><tr><th>Rep</th><th>Opener group</th><th>Opener</th></tr></thead>
            <tbody>
              {listed.map((o) => (
                <tr key={o.rep}>
                  <td>{o.name}</td>
                  <td>{o.in_group ? 'Member' : <span className="li-muted">-</span>}</td>
                  <td>
                    <PickList value={o.rule ?? 'default'} disabled={set.isPending} ariaLabel={`Opener setting for ${o.name}`}
                      onChange={(v) => set.mutate({ rep: o.rep, rule: v as 'include' | 'exclude' | 'default' })}
                      options={[{ value: 'default', label: o.in_group ? 'Yes (group)' : 'No' }, { value: 'include', label: 'Always' }, { value: 'exclude', label: 'Never' }]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogBody>
      <DialogFooter actions={<Button onClick={onClose}>Done</Button>} />
    </Dialog>
  );
}
